/**
 * Centralized User Role Type Definitions
 *
 * UserRoleEnum is the single source of truth for runtime role decisions.
 * Import from this file (or `@/types`) — do not hardcode role strings for auth.
 */

/**
 * Raw role string from API / localStorage (any format).
 * Always normalize via `normalizeRole` before comparisons.
 */
export type UserRole = string;

/**
 * Canonical role values for menus, routes, and permission checks.
 * Display strings match backend/menu config; keep "Study Cordinator" typo for compatibility.
 */
export enum UserRoleEnum {
  TENANT = "Tenant Admin",
  PRINCIPAL_INVESTIGATOR = "Principal Investigator",
  STUDY_COORDINATOR = "Study Cordinator", // Note: Keep typo for backward compatibility
  CRA = "CRA",
  CTA = "CTA",
  DATA_MANAGER = "Data Manager",
  PROJECT_MANAGER = "Project Manager",
  SPONSOR = "Sponsor",
  SITE_ADMIN = "Site Admin",
  QA_QC = "QA/QC",
  ACCOUNTANT = "Accountant",
  FM = "FM",
  REGULATORY = "Regulatory",
}

/**
 * Role display configuration
 * Used for UI rendering and visual representation
 */
export interface RoleConfig {
  name: string;
  color: string;
  icon: string;
}

/**
 * User role assignment interface
 * Represents a user's role in a specific study/site context
 */
export interface UserRoles {
  role: string;
  studyId: string;
  siteId?: string;
}

/**
 * Strip spaces / punctuation so "Tenant Admin", "TenantAdmin", "tenant-admin" match.
 */
const canonicalizeRoleKey = (role: string): string =>
  role.toLowerCase().replace(/[\s/\-_]+/g, "");

/**
 * Single alias table for API, menu, and legacy PascalCase / display names.
 * Keys must be output of `canonicalizeRoleKey`.
 */
export const ROLE_ALIASES: Record<string, UserRoleEnum> = {
  // Tenant
  tenantadmin: UserRoleEnum.TENANT,
  tenant: UserRoleEnum.TENANT,
  // PI
  principalinvestigator: UserRoleEnum.PRINCIPAL_INVESTIGATOR,
  // Study coordinator (+ typo + CRC)
  studycoordinator: UserRoleEnum.STUDY_COORDINATOR,
  studycordinator: UserRoleEnum.STUDY_COORDINATOR,
  crc: UserRoleEnum.STUDY_COORDINATOR,
  // CRA / Monitor
  cra: UserRoleEnum.CRA,
  cramonitor: UserRoleEnum.CRA,
  monitor: UserRoleEnum.CRA,
  clinicalresearchassociate: UserRoleEnum.CRA,
  // CTA
  cta: UserRoleEnum.CTA,
  // Data manager (+ aliases)
  datamanager: UserRoleEnum.DATA_MANAGER,
  dataanalyst: UserRoleEnum.DATA_MANAGER,
  datavalidator: UserRoleEnum.DATA_MANAGER,
  // Regulatory
  regulatory: UserRoleEnum.REGULATORY,
  // Sponsor
  sponsor: UserRoleEnum.SPONSOR,
  // Site admin
  siteadmin: UserRoleEnum.SITE_ADMIN,
  // Project manager
  projectmanager: UserRoleEnum.PROJECT_MANAGER,
  pm: UserRoleEnum.PROJECT_MANAGER,
  // QA/QC
  qaqc: UserRoleEnum.QA_QC,
  qaqualitycontrol: UserRoleEnum.QA_QC,
  // Accountant / Finance
  accountant: UserRoleEnum.ACCOUNTANT,
  fm: UserRoleEnum.FM,
  financemanager: UserRoleEnum.FM,
};

/** Enum display values keyed for O(1) direct match after canonicalize */
const ENUM_VALUE_KEYS: Record<string, UserRoleEnum> = Object.values(
  UserRoleEnum,
).reduce(
  (acc, value) => {
    acc[canonicalizeRoleKey(value)] = value;
    return acc;
  },
  {} as Record<string, UserRoleEnum>,
);

/**
 * Resolve a role string to UserRoleEnum, or null if unrecognized.
 * Prefer this when ingesting API menu keys so unknowns are skipped.
 */
export const tryNormalizeRole = (role: string): UserRoleEnum | null => {
  if (!role?.trim()) {
    return null;
  }
  if (Object.values(UserRoleEnum).includes(role as UserRoleEnum)) {
    return role as UserRoleEnum;
  }
  const key = canonicalizeRoleKey(role);
  return ROLE_ALIASES[key] ?? ENUM_VALUE_KEYS[key] ?? null;
};

/**
 * Convert any role string (API, PascalCase, display) to UserRoleEnum.
 * Unknown roles default to Tenant Admin (same behavior as before).
 */
export const normalizeRole = (role: string): UserRoleEnum => {
  return tryNormalizeRole(role) ?? UserRoleEnum.TENANT;
};

/**
 * True if `role` normalizes to `targetRole`.
 * Unknown / empty roles return false (strict — does not use normalizeRole's Tenant default).
 */
export const isRole = (role: string, targetRole: UserRoleEnum): boolean => {
  const normalized = tryNormalizeRole(role);
  return normalized !== null && normalized === targetRole;
};

/** True if `role` matches any of the given enum targets (strict; unknown → false). */
export const isAnyRole = (
  role: string,
  ...targets: UserRoleEnum[]
): boolean => {
  const normalized = tryNormalizeRole(role);
  return normalized !== null && targets.includes(normalized);
};

/** True if `role` is in the given set of enum values (strict; unknown → false). */
export const hasRole = (
  role: string,
  roles: ReadonlySet<UserRoleEnum>,
): boolean => {
  const normalized = tryNormalizeRole(role);
  return normalized !== null && roles.has(normalized);
};

/**
 * Role display names and colors for UI — keyed by UserRoleEnum.
 */
export const ROLE_DISPLAY_CONFIG: Record<UserRoleEnum, RoleConfig> = {
  [UserRoleEnum.TENANT]: {
    name: "Tenant Admin",
    color: "#0B5FFF",
    icon: "ShieldCheck",
  },
  [UserRoleEnum.DATA_MANAGER]: {
    name: "Data Manager",
    color: "#10B981",
    icon: "Database",
  },
  [UserRoleEnum.CRA]: {
    name: "CRA/Monitor",
    color: "#8B5CF6",
    icon: "ClipboardCheck",
  },
  [UserRoleEnum.CTA]: {
    name: "CTA",
    color: "#8B5CF6",
    icon: "ClipboardCheck",
  },
  [UserRoleEnum.PRINCIPAL_INVESTIGATOR]: {
    name: "Principal Investigator",
    color: "#EC4899",
    icon: "UserCheck",
  },
  [UserRoleEnum.STUDY_COORDINATOR]: {
    name: "Study Coordinator",
    color: "#F59E0B",
    icon: "Users",
  },
  [UserRoleEnum.SITE_ADMIN]: {
    name: "Site Admin",
    color: "#0FB6A6",
    icon: "Building",
  },
  [UserRoleEnum.SPONSOR]: {
    name: "Sponsor",
    color: "#3B82F6",
    icon: "Briefcase",
  },
  [UserRoleEnum.PROJECT_MANAGER]: {
    name: "Project Manager",
    color: "#FF9500",
    icon: "ChartBar",
  },
  [UserRoleEnum.QA_QC]: {
    name: "QA/QC",
    color: "#EF4444",
    icon: "ShieldCheck",
  },
  [UserRoleEnum.ACCOUNTANT]: {
    name: "Accountant",
    color: "#14B8A6",
    icon: "Receipt",
  },
  [UserRoleEnum.FM]: {
    name: "Finance Manager",
    color: "#6366F1",
    icon: "Wallet",
  },
  [UserRoleEnum.REGULATORY]: {
    name: "Regulatory",
    color: "#64748B",
    icon: "Scale",
  },
};
